// Copyright ©️ 2026 eVolve MEP, LLC

import { useCallback, useState } from 'react';

import { useEvolvePaginatedGet, type WorkCellTask, type WorkOrder, type WorkOrderPaginated } from '@evolvemep/api-ts';

import { getAllDataFromFetcher } from 'helpers/getAllDataFromFetcher';
import { isNil } from 'helpers/isNotNil';
import { useTaskStatuses } from 'hooks/typeCacheHooks/useTaskStatuses';

type ReturnType = {
  tasks: WorkCellTask[];
  fetchInProgressTasks: () => Promise<void>;
  loading: boolean;
};

export const useStartedTasks = (workOrder: WorkOrder | WorkOrderPaginated): ReturnType => {
  const { states: taskStatusTypes } = useTaskStatuses();
  const { fetchPage: fetchTasks } = useEvolvePaginatedGet('shop/workCellTask');
  const [allTasks, setAllTasks] = useState<WorkCellTask[]>([]);
  const [loading, setLoading] = useState(false);

  const fetchInProgressTasks = useCallback(async () => {
    const startedTaskStatusTypeIds = taskStatusTypes?.filter((t) => t.name !== 'Not Started').map((t) => t.stateId);
    if (isNil(startedTaskStatusTypeIds)) return;
    setAllTasks([]);
    setLoading(true);
    void getAllDataFromFetcher(fetchTasks, {
      params: {
        orderBy: 'createdOn:asc',
        workOrderId: workOrder.workOrderId,
        taskStatusTypeIds: startedTaskStatusTypeIds,
      },
    })
      .then(setAllTasks)
      .finally(() => setLoading(false));
  }, [fetchTasks, taskStatusTypes, workOrder.workOrderId]);

  return {
    fetchInProgressTasks,
    tasks: allTasks,
    loading,
  };
};
